import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./ClientPortfolioTimeline.css";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import EmailIcon from "@mui/icons-material/Email";
import CallIcon from "@mui/icons-material/Call";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import EventIcon from "@mui/icons-material/Event";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import { format } from "date-fns";
import axiosInstance from "../../../Config/axiosInstance";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ro", ro);

const TIMELINE_TYPES = ["Email", "Call", "Meeting"];

function ClientPortfolioTimeline({ clientData }) {
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [formData, setFormData] = useState({
    type: "Call",
    event_date: null,
    description: "",
  });

  const fetchTimeline = useCallback(async () => {
    if (!clientData?.id) {
      setTimelineData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await axiosInstance.get(
        `/timeline/?client=${clientData.id}`
      );

      setTimelineData(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError("Could not load the timeline.");
      setTimelineData([]);
    } finally {
      setLoading(false);
    }
  }, [clientData]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  const getTypeIcon = (type) => {
    switch (type) {
      case "Email":
        return <EmailIcon sx={{ fontSize: "16px" }} />;
      case "Call":
        return <CallIcon sx={{ fontSize: "16px" }} />;
      case "Meeting":
        return <MeetingRoomIcon sx={{ fontSize: "16px" }} />;
      default:
        return <EventIcon sx={{ fontSize: "16px" }} />;
    }
  };

  const formatGroupDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    return new Intl.DateTimeFormat("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  };

  const normalizeDate = (dateString) => {
    const date = new Date(dateString);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const getDateKey = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const toDateValue = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const groupByDate = useCallback((items) => {
    const groupsMap = items.reduce((acc, item) => {
      const key = getDateKey(item.event_date);

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(item);
      return acc;
    }, {});

    return Object.entries(groupsMap)
      .sort((a, b) => new Date(b[0]) - new Date(a[0]))
      .map(([dateKey, events]) => ({
        dateKey,
        displayDate: formatGroupDate(events[0]?.event_date),
        events: events.sort(
          (a, b) => new Date(b.event_date) - new Date(a.event_date)
        ),
      }));
  }, []);
  const { pastGroups, futureGroups, pastCount, futureCount } = useMemo(() => {
    const today = new Date();
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const sortedData = [...timelineData].sort(
      (a, b) => new Date(b.event_date) - new Date(a.event_date)
    );

    const past = [];
    const future = [];

    sortedData.forEach((item) => {
      const itemDate = normalizeDate(item.event_date);

      if (itemDate <= todayOnly) {
        past.push(item);
      } else {
        future.push(item);
      }
    });

    return {
      pastGroups: groupByDate(past),
      futureGroups: groupByDate(future),
      pastCount: past.length,
      futureCount: future.length,
    };
  }, [timelineData, groupByDate]);

  const resetForm = () => {
    setFormData({
      type: "Call",
      event_date: null,
      description: "",
    });
    setFormError("");
    setEditingItem(null);
  };

  const handleOpenAddModal = () => {
    resetForm();
    setModalMode("add");
    setModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setModalMode("edit");
    setEditingItem(item);
    setFormError("");
    setFormData({
      type: item?.type || "Call",
      event_date: toDateValue(item?.event_date),
      description: item?.description || "",
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (submitting) return;
    setModalOpen(false);
    setFormError("");
    setEditingItem(null);
  };

  const handleChangeForm = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleDateChange = (selectedDate) => {
    setFormData((prev) => ({
      ...prev,
      event_date: selectedDate,
    }));
  };

  const handleSubmit = async () => {
    if (!clientData?.id) {
      setFormError("Client is missing.");
      return;
    }

    if (
      !formData.type ||
      !formData.event_date ||
      !formData.description.trim()
    ) {
      setFormError("Please complete all fields.");
      return;
    }

    const payload = {
      client: clientData.id,
      type: formData.type,
      event_date: format(formData.event_date, "yyyy-MM-dd"),
      description: formData.description.trim(),
    };

    try {
      setSubmitting(true);
      setFormError("");

      if (modalMode === "add") {
        await axiosInstance.post("/timeline/", payload);
      } else {
        await axiosInstance.patch(`/timeline/${editingItem.id}/`, payload);
      }

      setModalOpen(false);
      resetForm();
      await fetchTimeline();
    } catch (err) {
      setFormError("Could not save the timeline entry.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderTimelineGroups = (groups, emptyMessage) => {
    if (!groups.length) {
      return (
        <div className="timeline-state timeline-empty">
          <EventIcon sx={{ fontSize: "48px", opacity: 0.35 }} />
          <p>{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="timeline-groups">
        {groups.map((group) => (
          <div key={group.dateKey} className="timeline-date-group">
            <div className="timeline-date-badge">{group.displayDate}</div>

            <div className="timeline-group-items">
              {group.events.map((item) => (
                <div key={item.id} className="timeline-row">
                  <div className="timeline-rail">
                    <div className="timeline-dot" />
                  </div>

                  <div className="timeline-event-card">
                    <button
                      type="button"
                      className="timeline-edit-btn"
                      onClick={() => handleOpenEditModal(item)}
                      aria-label="Edit timeline entry"
                    >
                      <EditIcon sx={{ fontSize: "16px" }} />
                    </button>

                    <div className="timeline-event-top">
                      <div className="timeline-event-type">
                        <span className="timeline-event-type-icon">
                          {getTypeIcon(item.type)}
                        </span>
                        <span>{item.type}</span>
                      </div>
                    </div>

                    <p className="timeline-event-description">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="timeline-crm">
        <div className="timeline-card">
          <div className="timeline-topbar">
            <div className="timeline-title-section">
              <div className="timeline-title-icon">
                <EventIcon sx={{ fontSize: "20px" }} />
              </div>
              <h2 className="timeline-main-title">
                Past interactions and upcoming events
              </h2>
            </div>

            <button
              className="timeline-followup-btn"
              type="button"
              onClick={handleOpenAddModal}
              disabled={!clientData?.id}
            >
              <NotificationsNoneIcon sx={{ fontSize: "18px" }} />
              Set up follow-up
            </button>
          </div>

          {loading ? (
            <div className="timeline-grid">
              <div className="timeline-column">
                <div className="timeline-column-header">
                  <h3>Past & Today</h3>
                </div>
                <div className="timeline-skeleton">
                  <div className="skeleton-item"></div>
                  <div className="skeleton-item"></div>
                  <div className="skeleton-item"></div>
                </div>
              </div>

              <div className="timeline-column">
                <div className="timeline-column-header">
                  <h3>Upcoming</h3>
                </div>
                <div className="timeline-skeleton">
                  <div className="skeleton-item"></div>
                  <div className="skeleton-item"></div>
                  <div className="skeleton-item"></div>
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="timeline-state timeline-error">
              <span>{error}</span>
            </div>
          ) : (
            <div className="timeline-grid">
              <div className="timeline-column">
                <div className="timeline-column-header">
                  <h3>Past & Today</h3>
                  <span>{pastCount} records</span>
                </div>

                {renderTimelineGroups(
                  pastGroups,
                  "No past or current events available"
                )}
              </div>

              <div className="timeline-column">
                <div className="timeline-column-header">
                  <h3>Upcoming</h3>
                  <span>{futureCount} records</span>
                </div>

                {renderTimelineGroups(
                  futureGroups,
                  "No upcoming events available"
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: "timeline-dialog-paper" }}
        className="timeline-dialog-root"
      >
        <DialogTitle className="timeline-dialog-title">
          <div className="timeline-dialog-title-row">
            <span className="timeline-dialog-title-text">
              {modalMode === "add"
                ? "Activity timeline add"
                : "Activity timeline edit"}
            </span>

            <IconButton
              onClick={handleCloseModal}
              disabled={submitting}
              className="timeline-dialog-close"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className="timeline-dialog-content" dividers>
          <div className="timeline-modal-form">
            <div className="timeline-modal-group">
              <label className="timeline-modal-label">Type <span style={{ color: "red" }}>*</span></label>
              <TextField
                select
                value={formData.type}
                onChange={handleChangeForm("type")}
                fullWidth
                className="timeline-modal-field"
              >
                {TIMELINE_TYPES.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            <div className="timeline-modal-group">
              <label className="timeline-modal-label">Date <span style={{ color: "red" }}>*</span></label>

              <div className="timeline-datepicker-wrapper">
                <DatePicker
                  selected={formData.event_date}
                  onChange={handleDateChange}
                  dateFormat="dd.MM.yyyy"
                  locale="ro"
                  placeholderText="dd.mm.yyyy"
                  className="timeline-datepicker-input"
                  wrapperClassName="timeline-datepicker-full"
                />
                <span className="timeline-datepicker-icon">
                  <CalendarTodayIcon sx={{ fontSize: "18px" }} />
                </span>
              </div>
            </div>

            <div className="timeline-modal-group">
              <label className="timeline-modal-label">Description <span style={{ color: "red" }}>*</span></label>
              <TextField
                value={formData.description}
                onChange={handleChangeForm("description")}
                fullWidth
                multiline
                minRows={8}
                className="timeline-modal-field timeline-modal-textarea"
                placeholder="Enter description"
              />
            </div>

            {formError ? (
              <div className="timeline-modal-submit-error">{formError}</div>
            ) : null}
          </div>
        </DialogContent>

        <DialogActions className="timeline-dialog-actions">
          <Button
            onClick={handleCloseModal}
            disabled={submitting}
            className="timeline-modal-btn timeline-modal-btn-cancel"
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="timeline-modal-btn timeline-modal-btn-save"
          >
            {submitting ? (
              <>
                <CircularProgress size={16} color="inherit" />
                Saving
              </>
            ) : modalMode === "add" ? (
              "Save"
            ) : (
              "Update"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ClientPortfolioTimeline;